import {
  flattenFueFlags,
  splitFueFlags,
} from "@/features/feature-tour/fue-flags";
import { logError } from "@/logging/logging";
import type { Feature, FeatureFlag } from "@/store/features/features";
import { FaroText, useToast } from "@faro-lotv/flat-ui";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Stack, TableContainer } from "@mui/material";
import { useState, type JSX } from "react";
import { DevFeaturesTable } from "./dev-features-table";
import { DevToolsTable } from "./dev-tools-table";

type DevPanelProps = {
  entries: Array<[FeatureFlag, Feature]>;
  searchText: string;
};

/** @returns The experimental features and developer tools panels. */
export function DevPanel({ entries, searchText }: DevPanelProps): JSX.Element {
  const { coreApiClient } = useApiClientContext();
  const { openToast } = useToast();
  const [isResetting, setIsResetting] = useState(false);

  async function resetFueFlags(): Promise<void> {
    setIsResetting(true);
    try {
      const rawFueFlags = await coreApiClient.getUserFueFlags();
      const fueFlags = splitFueFlags(rawFueFlags);
      await coreApiClient.setUserFueFlags(
        flattenFueFlags({ known: {}, other: fueFlags.other }),
      );
      openToast({ title: "Feature tour flags reset.", variant: "success" });
      window.location.reload();
    } catch (error) {
      logError(error);
      openToast({
        title: "Failed to reset feature tour flags.",
        variant: "error",
      });
      setIsResetting(false);
    }
  }

  return (
    <Stack sx={{ gap: 4 }}>
      <Stack sx={{ gap: 2 }}>
        <FaroText variant="heading12">Features</FaroText>
        <TableContainer
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <DevFeaturesTable entries={entries} />
        </TableContainer>
      </Stack>
      <Stack sx={{ gap: 2 }}>
        <FaroText variant="heading12">Developer Tools</FaroText>
        <TableContainer
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <DevToolsTable
            isResetting={isResetting}
            onResetFueFlags={resetFueFlags}
            searchText={searchText}
          />
        </TableContainer>
      </Stack>
    </Stack>
  );
}
